/* eslint-disable  */
import { useContext, useCallback } from 'react';
import Cookies from 'js-cookie';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import useValidateSettings from './useValidateSettings';

import ajax from '../utils/ajaxHelper';
import { getHostType } from '../utils';
import { CLASS_INSTANCE_EVENTS, CLASS_INSTANCE_INSIGHTS } from '../constants/actionTypes';
import { ClassInstanceContext } from '../contexts/classInstanceContext';
import useAuth from './useAuth';
import useClientSignup from './useClientSignup';

import useAlert from './useAlert';
import useUtilities from './useUtilities';
import moment from 'moment';
import { EventType } from '../constants/globalConstants';
import { ClientSignupContext } from '../contexts/ClientSignupContext';
import { API_EVENTS } from '../constants/actionTypes';

const useClassInstance = () => {
  const [classInstanceStates, classInstanceDispatch] = useContext(ClassInstanceContext);
  const [clientSignupState, clientSignupDispatch] = useContext(ClientSignupContext);
  const { getClientDetails } = useClientSignup();
  const [cookies] = useCookies();
  const navigate = useNavigate();
  const { updateSettings } = useValidateSettings();
  const { fetchCompanyId } = useAuth();
  const { alert } = useAlert();
  const { isSolo, isSoloClient, isLoggedIn } = useUtilities();

  const { roleId, clientId } = useParams();
  const hosttype = getHostType(roleId);
  const isClientLogin = isSoloClient() && isLoggedIn();

  const getClassInstanceOptions = useCallback(async () => {
    try {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
      const companyId = await fetchCompanyId();
      if (companyId) {
        const res = await ajax.post('get-class-instance-options', {
          companyId,
        });
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: {
            ...res.data,
            instructors: res?.data?.instructors.sort((x, y) => {
              /* eslint-disable  no-nested-ternary */
              const a = x?.userId?.firstName?.toUpperCase();
              const b = y?.userId?.firstName?.toUpperCase();
              return a === b ? 0 : a > b ? 1 : -1;
            }),
          },
        });
      } else {
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.FAIL,
          payload: new Error('Company is not available'),
        });
      }
    } catch (error) {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
    }
  }, [classInstanceDispatch, fetchCompanyId]);

  const getClassInstanceList = useCallback(
    async (date) => {
      if (!date) {
        date = localStorage.getItem('selectedCalendarDate');
      }
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        date = moment(date).format('YYYY-MM-DD');
        if (companyId) {
          const res = await ajax.post(
            'class-instance-list',
            {
              companyId,
              date,
            },
            {
              networkParams: { showLoader: true },
            },
            {
              headers: {
                hosttype,
              },
            },
          );
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.SET,
            payload: {
              classInstanceSignedUpCount: res?.classInstanceSignedUpCount || {},
              calendars: res?.data
                .sort((x, y) => {
                  /* eslint-disable  no-nested-ternary */
                  const a = x?.classId?.className?.toUpperCase();
                  const b = y?.classId?.className?.toUpperCase();
                  return a === b ? 0 : a > b ? 1 : -1;
                })
                .sort((x, y) => {
                  const a = x?.startTime?.toUpperCase();
                  const b = y?.startTime?.toUpperCase();
                  return a === b ? 0 : a > b ? 1 : -1;
                }),
            },
          });
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, hosttype, fetchCompanyId],
  );

  const getClassInstanceDetails = useCallback(
    async (id) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        if (companyId) {
          const res = await ajax.post('class-instance-details', {
            companyId,
            id,
          });
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.SET,
            payload: {
              classInstance: res.data,
            },
          });
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, fetchCompanyId],
  );

  const getClassInstanceRoaster = useCallback(
    async (id, callback, isABP = false) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        if (companyId) {
          const res = await ajax.post('class-instance-roaster', {
            companyId,
            id,
            isABP,
          });

          if (res?.data) {
            callback(res.data);
          }
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, fetchCompanyId],
  );

  const createOrUpdateABPClassInstance = useCallback(
    async (values, { setErrors, setStatus, setSubmitting }, callback, isABI = false) => {
      try {
        let updateClassAPI = 'create-or-update-abp-class-instance';
        if(values?.recurringClass) {
          updateClassAPI = 'create-or-update-recurring-class-instance';
        }
        setSubmitting(true);
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        let data = { ...values, isABI };

        if (companyId) {
          const response = await ajax.post(
            updateClassAPI,
            {
              ...data,
              companyId,
            },
            {
              networkParams: { showLoader: false },
            },
          );
          setStatus({ success: true });
          classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
          callback(response?.ClassInstanceId);
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
          setStatus({ success: false });
          setErrors({ submit: new Error('Company is not available') });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
        setStatus({ success: false });
        setErrors({ submit: error.message });
      } finally {
        setSubmitting(false);
      }
    },
    [classInstanceDispatch, fetchCompanyId],
  );

  const setClassInstancePopup = useCallback(
    async (classInstancePopup) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { classInstancePopup } });
    },
    [classInstanceDispatch],
  );

  const setInvalidPackModal = useCallback(
    async (invalidPackModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { invalidPackModal } });
    },
    [classInstanceDispatch],
  );

  const setInvalidPackMessageModal = useCallback(
    async (invalidPackMessageModal) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET,
        payload: { invalidPackMessageModal },
      });
    },
    [classInstanceDispatch],
  );

  const setClassInstanceId = useCallback(
    async (classInstanceId) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { classInstanceId } });
    },
    [classInstanceDispatch],
  );

  const setClassesFilter = useCallback(
    async (classesFilter) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET_CLASSES_FILTER,
        payload: classesFilter,
      });
    },
    [classInstanceDispatch],
  );

  const setStudioFilter = useCallback(
    async (studioFilter) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET_STUDIO_FILTER,
        payload: studioFilter,
      });
    },
    [classInstanceDispatch],
  );

  const setTimePeriod = useCallback(
    async (timePeriodFilter) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET_TIMEPERIOD_FILTER,
        payload: timePeriodFilter,
      });
    },
    [classInstanceDispatch],
  );

  const setInstructorFilter = useCallback(
    async (instructorFilter) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET_INSTRUCTOR_FILTER,
        payload: instructorFilter,
      });
    },
    [classInstanceDispatch],
  );

  const resetFilters = useCallback(async () => {
    classInstanceDispatch({
      type: CLASS_INSTANCE_EVENTS.RESET_FILTER,
    });
  }, [classInstanceDispatch]);

  const setABP = useCallback(async () => {
    const isABP = !!parseInt(Cookies.get('isABP'), 10);
    classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { isABP } });
  }, [classInstanceDispatch]);

  /* calendar page - studio - start */

  const setStudioModal = useCallback(
    async (studioModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { studioModal } });
    },
    [classInstanceDispatch],
  );

  const setClientProfileModal = useCallback(
    async (clientProfileModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { clientProfileModal } });
    },
    [classInstanceDispatch],
  );

  const setAddNotesModal = useCallback(
    async (addNotesModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { addNotesModal } });
    },
    [classInstanceDispatch],
  );

  const setClientProfileData = useCallback(
    async (clientProfileData) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { clientProfileData } });
    },
    [classInstanceDispatch],
  );

  const createOrUpdateABPStudio = useCallback(
    async (values, { setErrors, setStatus, setSubmitting }, callback) => {
      const { redirectPath, ...data } = values;

      try {
        setSubmitting(true);
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        if (companyId) {
          const response = await ajax.post(
            'create-or-update-abp-studio',
            {
              ...data,
              companyId,
            },
            {
              networkParams: { showLoader: false },
            },
          );
          setStatus({ success: true });
          classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
          if (redirectPath) {
            updateSettings(redirectPath);
          }
          callback(response?.studioId);
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
          setStatus({ success: false });
          setErrors({ submit: new Error('Company is not available') });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
        setStatus({ success: false });
        setErrors({ submit: error.message });
      } finally {
        setSubmitting(false);
      }
    },
    [classInstanceDispatch, fetchCompanyId, updateSettings],
  );

  const getStudioById = useCallback(
    async (values, callback) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        if (companyId) {
          const response = await ajax.post(
            'get-studio-by-id',
            {
              ...values,
              companyId,
            },
            /* {
              networkParams: { showLoader: false },
            }, */
          );
          classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
          callback(response?.data || []);
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, fetchCompanyId],
  );

  const removeStudio = useCallback(
    async (studioId) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        await ajax.post('remove-studio', {
          studioId,
        });
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch],
  );

  /* calendar page - studio - end */

  /* calendar page - room - start */

  const setRoomModal = useCallback(
    async (roomModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { roomModal } });
    },
    [classInstanceDispatch],
  );

  const setRoomEditModal = useCallback(
    async (roomEditModal) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { roomEditModal } });
    },
    [classInstanceDispatch],
  );

  const setRoomEditModalDetails = useCallback(
    async (roomEditModalValues) => {
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET,
        payload: { roomEditModalValues: roomEditModalValues },
      });
    },
    [classInstanceDispatch],
  );

  const createOrUpdateABPRoom = useCallback(
    async (values, { setErrors, setStatus, setSubmitting }, callback) => {
      try {
        setSubmitting(true);
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const companyId = await fetchCompanyId();
        if (companyId) {
          const response = await ajax.post(
            'create-or-update-abp-room',
            {
              ...values,
              companyId,
            },
            {
              networkParams: { showLoader: false },
            },
          );
          setStatus({ success: true });
          classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
          callback(response?.roomId);
        } else {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.FAIL,
            payload: new Error('Company is not available'),
          });
          setStatus({ success: false });
          setErrors({ submit: new Error('Company is not available') });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
        setStatus({ success: false });
        setErrors({ submit: error.message });
      } finally {
        setSubmitting(false);
      }
    },
    [classInstanceDispatch, fetchCompanyId],
  );

  /* Check if location name is available for the room */
  const checkIfLocationNameIsAvailable = async (values) => {
    try {
      const companyId = await fetchCompanyId();
      if (companyId) {
        const response = await ajax.post(
          'check-if-location-name-is-available',
          {
            ...values,
            companyId,
          },
          {
            networkParams: { showLoader: false },
          },
        );
        return response;
      } else {
        return { status: true, locationAvailable: false, msg: 'companyId Not Found', data: [] };
      }
    } catch (error) {
      return { status: true, locationAvailable: false, msg: error, data: [] };
    }
  };

  /* calendar page - room - end */
  const getBookedClassList = useCallback(async () => {
    try {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
      const res = await ajax.post('booked-class-list', {});
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET,
        payload: {
          bookedClassList: res.data,
        },
      });
    } catch (error) {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
    }
  }, [classInstanceDispatch]);

  const getClientReportByUserId = useCallback(
    async (clientSignupDetailsId) => {
      try {
        // const companyId = await fetchCompanyId();
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        let body = {
          isSolo: isSolo(),
        };
        //if (clientId !== '' || clientId != undefined) {
        body.clientSignupDetailsId = clientSignupDetailsId || clientId;
        //}
        const response = await ajax.post('get-client-class-details', body, {
          networkParams: { showLoader: false },
        });
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
        if (response?.data) {
          classInstanceDispatch({
            type: CLASS_INSTANCE_EVENTS.SET,
            payload: { clientData: response?.data, packageCount: response?.count || 0 },
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, isSolo],
  );

  const registerClass = useCallback(
    async (classInstanceId, userId, date, isABP = false, callback, isGuest = false, showLoader = false) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: { processingSignUpReq: true },
        });
        const isChecked = sessionStorage.getItem('checkIn') === '1' || false;
        console.log('classInstanceId bookclass', classInstanceId)
        const response = await ajax.post(
          'book-class',
          {
            classInstanceId,
            userId,
            isABP,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            isChecked,
          },
          {
            networkParams: { showLoader: showLoader },
          },
        );
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
        if (response?.error === 'InvalidPack') {
          let freezeError = '';
          response?.packagesDetails.map((packError) => {
            packError?.message.map((mess) => {
              if (mess.freezeError) freezeError = mess.freezeError;
            });
          });

          sessionStorage.setItem('classInstanceId', classInstanceId);
          await setInvalidPackMessageModal(freezeError);
          await setInvalidPackModal(true);
        } else if (response?.error === 'AlreadyRegistered') {
          navigate('../../../event/cancel-event/' + classInstanceId);
        } else if (response?.error === 'ClassFull') {
          navigate(`../../../event/fully-booked/${classInstanceId}`);
        } else {
          await alert({
            message: response?.status
              ? 'You have been registered for this session!'
              : 'Internal server error, try again.',
          });
          if (sessionStorage.getItem('isABI')) {
            sessionStorage.removeItem('checkIn');
            navigate('../../../event/booked-event/' + classInstanceId);
          }
        }
        
        if(isGuest && callback) {
          return callback();
        }

        if (
          response?.status &&
          !(
            response?.error === 'InvalidPack' ||
            response?.error === 'AlreadyRegistered' ||
            response?.error === 'ClassFull'
          )
        ) {
          await getBookedClassList();
          await getClassInstanceList(date);
          await getClientReportByUserId();
          if (callback) return callback();
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
        console.log(error);
      } finally {
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: { processingSignUpReq: false, requestedClassInstanceId: null },
        });
      }
    },
    [
      alert,
      classInstanceDispatch,
      getBookedClassList,
      getClassInstanceList,
      setInvalidPackModal,
      getClientReportByUserId,
    ],
  );

  const cancelClass = useCallback(
    async (classInstanceId, date, callback) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: { processingSignUpReq: true, requestedClassInstanceId: classInstanceId },
        });

        const response = await ajax.post(
          'cancel-class',
          {
            classInstanceId,
          },
          {
            networkParams: { showLoader: false },
          },
        );
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
        await alert({
          message: response?.status
            ? 'You have cancelled your session!'
            : 'Internal server error, try again.',
        });
        if (callback) {
          return callback();
        }
        if (response?.status) {
          await getBookedClassList();
          await getClassInstanceList(date);
          await getClientReportByUserId();
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      } finally {
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: { processingSignUpReq: false, requestedClassInstanceId: null },
        });
      }
    },
    [
      alert,
      classInstanceDispatch,
      getBookedClassList,
      getClassInstanceList,
      getClientReportByUserId,
    ],
  );

  const validateClassRegistration = useCallback(
    async (
      classInstanceId,
      userId = null,
      date,
      isABP = false,
      callback,
      detailsUpdate = false,
    ) => {
      // store in session classInstanceId
      classInstanceDispatch({
        type: CLASS_INSTANCE_EVENTS.SET,
        payload: { requestedClassInstanceId: classInstanceId },
      });
      // check the user is logged in or not
      if (!cookies.token) {
        // redirect to login page

        if (sessionStorage.getItem('isABI')) {
          // navigate('../../../login');
          navigate({
            pathname: '../../../login',
            search: '?abi=true',
          });
        } else if (isABP) {
          // navigate('../../../login');
          navigate({
            pathname: '../../../login',
            search: '?details=true',
          });
        } else {
          navigate(isSolo() ? 'login' : '../user/login');
        }
        sessionStorage.setItem('classInstanceId', classInstanceId);
      } else {
        /* if (
          '66290c02aab42c45cc6dc157' === classInstanceId ||
          '662bb254d9294523784dec62' === classInstanceId ||
          '66290c02aab42c45cc6dc158' === classInstanceId ||
          '662bb254d9294523784dec64' === classInstanceId ||
          '6606a8b3aecaf74bd05c21fb' === classInstanceId
        ) {
          await registerClass(classInstanceId, userId, date, isABP, callback);
        } else */
        if (detailsUpdate) {
          await checkUserUpdateNeeded(classInstanceId, userId, date, isABP, callback);
        } else {
          await registerClass(classInstanceId, userId, date, isABP, callback);
        }
      }
    },
    [cookies.token, navigate, registerClass, classInstanceDispatch, isSolo],
  );
  const isEvent = (type, eventType) => {
    return eventType === type;
  };
  const checkUserUpdateNeeded = async (classInstanceId, userId, date, isABP, callback) => {
    const userData = await getClientDetails();

    const companyId = await fetchCompanyId();
    const eventDetails = await ajax.post(
      'get-event-details',
      {
        companyId,
        eventId: classInstanceId,
      },
      {
        networkParams: { showLoader: false },
      },
    );
    const eventType = eventDetails?.data?.classId?.eventId;
    const userAcount = userData?.clientAccountDetails;
    if (
      (sessionStorage.getItem('isABI') &&
        (((isEvent(EventType?.FitnessAndWellness, eventType) ||
          isEvent(EventType?.ArtsAndCulture, eventType) ||
          isEvent(EventType?.BoardMeeting, eventType) ||
          isEvent(EventType?.Ceremonies, eventType) ||
          isEvent(EventType?.CommunityEvents, eventType) ||
          isEvent(EventType?.Tours, eventType) ||
          isEvent(EventType?.VolunteerEvents, eventType)) &&
          (!userAcount?.firstName ||
            !userAcount?.lastName ||
            !userAcount?.emailId ||
            !userAcount?.gender ||
            !userAcount?.address1 ||
            !userAcount?.city ||
            !userAcount?.state ||
            !userAcount?.zip ||
            !userAcount?.raceEthnicity ||
            // !userAcount?.neighborhood ||
            // !userAcount?.subarea ||
            // !userAcount?.npu ||
            !userAcount?.income)) ||
          (isEvent(EventType?.BusinessSupport, eventType) &&
            (!userAcount?.firstName ||
              !userAcount?.lastName ||
              !userAcount?.emailId ||
              !userAcount?.phone ||
              !userAcount?.gender ||
              !userAcount?.address1 ||
              !userAcount?.city ||
              !userAcount?.state ||
              !userAcount?.zip ||
              !!!userAcount?.businessOwner?.toString())) ||
          (isEvent(EventType?.ResidentSupport, eventType) &&
            (!userAcount?.firstName ||
              !userAcount?.lastName ||
              !userAcount?.emailId ||
              !userAcount?.gender ||
              !userAcount?.address1 ||
              !userAcount?.city ||
              !userAcount?.state ||
              !userAcount?.zip ||
              !userAcount?.raceEthnicity ||
              // !userAcount?.neighborhood ||
              // !userAcount?.subarea ||
              // !userAcount?.npu ||
              !userAcount?.income ||
              // !userAcount?.propertyIdNumber ||
              // !userAcount?.neighborhoodCode ||
              !userAcount?.hearAboutOurWorkshop)))) ||
      (!(
        isEvent(EventType?.FitnessAndWellness, eventType) ||
        isEvent(EventType?.ArtsAndCulture, eventType) ||
        isEvent(EventType?.BoardMeeting, eventType) ||
        isEvent(EventType?.Ceremonies, eventType) ||
        isEvent(EventType?.CommunityEvents, eventType) ||
        isEvent(EventType?.VolunteerEvents, eventType) ||
        isEvent(EventType?.BusinessSupport, eventType) ||
        isEvent(EventType?.Tours, eventType) ||
        isEvent(EventType?.ResidentSupport, eventType)
      ) &&
        (!userAcount?.firstName ||
          !userAcount?.lastName ||
          !userAcount?.emailId ||
          !userAcount?.phone ||
          !userAcount?.dob ||
          !userAcount?.gender ||
          !userAcount?.address1 ||
          !userAcount?.city ||
          !userAcount?.state ||
          !userAcount?.zip ||
          !userAcount?.emergencyContact?.firstName ||
          !userAcount?.emergencyContact?.lastName ||
          !userAcount?.emergencyContact?.emailId ||
          !userAcount?.emergencyContact?.phone ||
          !userAcount?.emergencyContact?.relationShip ||
          userData?.contract?.isContractSignatureTermsAgreed !== '1' ||
          userData?.waiver?.isSignatureTermsAgreed !== '1'))
    ) {
      navigate('../../../book-event/account-details/' + classInstanceId);
      return true;
    }
    await registerClass(classInstanceId, userId, date, isABP, callback);
    return false;
  };

  const deleteClassInstance = useCallback(
    async (id, recurringEvent = null) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        await ajax.post('delete-class-instance', {
          id,
          recurringEvent,
        });

        setTimeout(async () => {
          await getBookedClassList();
          await getClassInstanceList();
        }, 500);
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch, getClassInstanceList, getBookedClassList],
  );

  const showBackButton = useCallback(
    async (backButtonEnabled) => {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SET, payload: { backButtonEnabled } });
    },
    [classInstanceDispatch],
  );

  const updateAttendance = useCallback(
    async (data, callback) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const response = await ajax.post('update-attendance', data, {
          networkParams: { showLoader: false },
        });
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });

        if (response?.status && callback) {
          await callback();
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch],
  );

  const updateClientInfo = useCallback(
    async (data, callback) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
        const response = await ajax.post('update-client-info', data, {
          networkParams: { showLoader: false },
        });
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });

        if (response?.status && callback) {
          await callback();
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [classInstanceDispatch],
  );

  const removeClient = useCallback(
    async (data, callback) => {
      try {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });

        const response = await ajax.post('remove-client', data, {
          networkParams: { showLoader: false },
        });
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });

        if (response?.status) {
          callback();
          await alert({
            message: response?.status
              ? 'The client has been removed successfully'
              : 'Internal server error, try again.',
          });
        }
      } catch (error) {
        classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
      }
    },
    [alert, classInstanceDispatch],
  );

  const getClientReport = useCallback(async () => {
    try {
      const companyId = await fetchCompanyId();
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.REQUEST });
      const response = await ajax.post(
        'get-client-report',
        { companyId, isSolo: isSolo() },
        {
          networkParams: { showLoader: false },
        },
      );
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.SUCCESS });
      if (response?.data) {
        classInstanceDispatch({
          type: CLASS_INSTANCE_EVENTS.SET,
          payload: { clientReport: response?.data },
        });
      }
    } catch (error) {
      classInstanceDispatch({ type: CLASS_INSTANCE_EVENTS.FAIL, payload: error });
    }
  }, [classInstanceDispatch, isSolo, fetchCompanyId]);

  const getClientInsights = useCallback(async () => {
    try {
      const companyId = await fetchCompanyId();
      classInstanceDispatch({ type: CLASS_INSTANCE_INSIGHTS.REQUEST });
      const response = await ajax.post(
        'get-client-insights',
        { companyId, isSolo: isSolo() },
        {
          networkParams: { showLoader: false },
        },
      );
      classInstanceDispatch({ type: CLASS_INSTANCE_INSIGHTS.SUCCESS });
      if (response?.data) {
        classInstanceDispatch({
          type: CLASS_INSTANCE_INSIGHTS.SET,
          payload: { clientInsights: response?.data },
        });
      }
    } catch (error) {
      classInstanceDispatch({ type: CLASS_INSTANCE_INSIGHTS.FAIL, payload: error });
    }
  }, [classInstanceDispatch, isSolo, fetchCompanyId]);

  const createClientAccountAndEventSignUp = useCallback(
    async (values, { resetForm, setStatus, setSubmitting }, handleNxt, page = '') => {
      const { redirectPath, ...data } = values;
      delete data.isStaffEmailIdExists;
      clientSignupDispatch({ type: API_EVENTS.REQUEST });
      const companyIdTemp = await fetchCompanyId();
      try {
        const res = await ajax.post(
          'create-client-account',
          {
            ...data,
            //  gender: data?.gender === '3' ? null : data?.gender,
            companyId: companyIdTemp,
            login: true,
          },
          {
            networkParams: { showLoader: false },
            headers: {
              hosttype,
            },
          },
        );
        resetForm();
        setStatus({ success: true });
        clientSignupDispatch({
          type: API_EVENTS.SUCCESS,
          payload: {
            // handleNext: !isClientLogin,
            clientAccountDetails: {
              ...data,
              stateName: res?.stateName || '',
              emergencyContact: {
                ...data?.emergencyContact,
                _id: res.emergencyContactId,
              },
              clientSignupDetailsId: res.clientSignupDetailsId,
              userId: res.userId,
              contactId: res.contactId,
            },
          },
        });

        if (data?._id) {
          await validateClassRegistration(
            values?.classInstance?._id, // classInstanceId
            null, // userId
            moment(res?.data?.date).startOf('week').toDate(), // date
            true, // isABP
          );
        } else {
          await registerClass(
            values?.classInstance?._id,
            res?.data?.userId,
            moment(res?.data?.date).startOf('week').toDate(),
            true,
            () => {},
          );
        }
      } catch (error) {
        clientSignupDispatch({
          type: API_EVENTS.FAIL,
          payload: error,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [clientSignupDispatch, updateSettings, navigate, isClientLogin],
  );

  return {
    createOrUpdateABPClassInstance,
    createOrUpdateABPStudio,
    createOrUpdateABPRoom,
    checkIfLocationNameIsAvailable,
    getClassInstanceDetails,
    getClassInstanceRoaster,
    getClassInstanceOptions,
    getClassInstanceList,
    setClassInstancePopup,
    setInvalidPackModal,
    setStudioModal,
    setRoomModal,
    setRoomEditModal,
    setRoomEditModalDetails,
    setClientProfileModal,
    setClientProfileData,
    setClassesFilter,
    setInstructorFilter,
    setABP,
    setClassInstanceId,
    resetFilters,
    classInstanceStates,
    validateClassRegistration,
    getBookedClassList,
    cancelClass,
    deleteClassInstance,
    showBackButton,
    getStudioById,
    removeStudio,
    updateAttendance,
    getClientReport,
    setStudioFilter,
    setTimePeriod,
    updateClientInfo,
    setAddNotesModal,
    removeClient,
    getClientReportByUserId,
    getClientInsights,
    createClientAccountAndEventSignUp,
    registerClass,
  };
};

export default useClassInstance;
